import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronRight, Copy, Crosshair, ExternalLink, Film, Loader2, RotateCcw, Search, Trash2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { useIsMobile } from "../lib/useIsMobile";
import { batchDeleteRequests, listRequests, retryRequest, type RequestRecord, type RequestStatus } from "../api";

interface Props {
  /** Jump to the Backlog to render this scan's remaining candidates. */
  onOpenBacklog: () => void;
  /** Open a finished render in Job Detail (used by "Open render" on a duplicate-of-rendered result). */
  onOpenJob: (jobId: string) => void;
}

const UNDO_MS = 5000;

export function Requests({ onOpenBacklog, onOpenJob }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<RequestRecord[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<{ ids: string[] } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try { setItems(await listRequests()); }
    catch { setItems((cur) => cur ?? []); }
  }, []);

  const retry = useCallback(async (id: string) => {
    try { await retryRequest(id); }
    catch (e) {
      setNotice(e instanceof Error ? e.message : "Retry failed");
      window.setTimeout(() => setNotice((n) => (n === (e instanceof Error ? e.message : "Retry failed") ? null : n)), 4000);
      return;
    }
    await refresh();
  }, [refresh]);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => { void refresh(); }, 4000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const commitDelete = useCallback(async (ids: string[]) => {
    try { await batchDeleteRequests(ids); } catch { /* reconciled by the next refresh */ }
    timerRef.current = null;
    setPending(null);
    void refresh();
  }, [refresh]);

  const startDelete = (ids: string[]): void => {
    if (ids.length === 0) return;
    if (timerRef.current !== null) { window.clearTimeout(timerRef.current); if (pending) void commitDelete(pending.ids); }
    setSelected(new Set());
    setPending({ ids });
    timerRef.current = window.setTimeout(() => { void commitDelete(ids); }, UNDO_MS);
  };
  const undo = (): void => {
    if (timerRef.current !== null) { window.clearTimeout(timerRef.current); timerRef.current = null; }
    setPending(null);
  };
  useEffect(() => () => { if (timerRef.current !== null) window.clearTimeout(timerRef.current); }, []);

  const copy = async (text: string, key: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1200);
    } catch { /* clipboard blocked — ignore */ }
  };

  const pendingIds = new Set(pending?.ids ?? []);
  const visible = (items ?? []).filter((r) => !pendingIds.has(r.id));
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const toggle = (id: string): void => setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = (): void => setSelected(allSelected ? new Set() : new Set(visible.map((r) => r.id)));

  return (
    <div>
      <PageHeader
        eyebrow="Requests"
        title="Scans you've run."
        subtitle="Every auto-scan and 'find a scene' you've launched — with the candidates it dropped in the Backlog and how many you haven't rendered yet. Persisted across redeploys, so you can come back to the leftovers later. (Renders live in the Render queue.)"
      />

      {items === null ? (
        <Muted>Loading…</Muted>
      ) : visible.length === 0 ? (
        <Empty />
      ) : (
        <div style={PANEL}>
          <div style={TOOLBAR}>
            <Tick checked={allSelected} onChange={toggleAll} />
            <span style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>{selected.size > 0 ? `${selected.size} selected` : "Select all"}</span>
            {selected.size > 0 && (
              <button type="button" onClick={() => startDelete([...selected])} style={DANGER_BTN}>
                <Trash2 size={14} strokeWidth={2} /> Delete {selected.size}
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)" }}>
              {visible.length} scan{visible.length === 1 ? "" : "s"}
            </span>
          </div>
          {visible.map((r, i) => (
            <Row
              key={r.id}
              r={r}
              isLast={i === visible.length - 1}
              isMobile={isMobile}
              selected={selected.has(r.id)}
              onToggle={() => toggle(r.id)}
              onOpenBacklog={onOpenBacklog}
              onOpenJob={onOpenJob}
              onRetry={() => retry(r.id)}
              onDelete={() => startDelete([r.id])}
              copied={copied}
              onCopy={copy}
            />
          ))}
        </div>
      )}

      {pending && (
        <div style={TOAST}>
          <span style={{ fontSize: 13 }}>Deleted {pending.ids.length}</span>
          <button type="button" onClick={undo} style={UNDO_BTN}><RotateCcw size={13} strokeWidth={2.2} /> Undo</button>
        </div>
      )}

      {notice && (
        <div style={TOAST}>
          <span style={{ fontSize: 13, color: "#F87171" }}>{notice}</span>
        </div>
      )}
    </div>
  );
}

function Row({
  r, isLast, isMobile, selected, onToggle, onOpenBacklog, onOpenJob, onRetry, onDelete, copied, onCopy,
}: {
  r: RequestRecord;
  isLast: boolean;
  isMobile: boolean;
  selected: boolean;
  onToggle: () => void;
  onOpenBacklog: () => void;
  onOpenJob: (jobId: string) => void;
  onRetry: () => void | Promise<void>;
  onDelete: () => void;
  copied: string | null;
  onCopy: (text: string, key: string) => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const b = badge(r.status);
  const title = r.sourceTitle || r.url;
  const KindIcon = r.kind === "locate" ? Crosshair : Search;
  const hasRender = Boolean(r.relatedJobId);
  // Retry makes sense for a run that produced nothing usable: failed, interrupted
  // by a restart, or finished with no candidates AND nothing on the shelf AND not
  // a dup-of-rendered (those get "Open render" instead).
  const canRetry =
    r.status === "failed" ||
    r.status === "interrupted" ||
    (r.status === "scanned" && (r.candidateCount ?? 0) === 0 && r.pendingCount === 0 && !hasRender);
  const handleRetry = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await onRetry(); } finally { setBusy(false); }
  };
  return (
    <div
      onClick={onOpenBacklog}
      title="Open the Backlog to render this scan's candidates"
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: isMobile ? "12px 14px" : "14px 18px",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,.04)",
        cursor: "pointer", transition: "background .15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.02)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <Tick checked={selected} onChange={onToggle} />
      <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(96,165,250,.10)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#60A5FA", flexShrink: 0 }}>
        <KindIcon size={15} strokeWidth={2} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        <div style={{ fontSize: 11, color: r.status === "failed" ? "#F87171" : r.status === "interrupted" ? "#FB923C" : "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r.kind === "locate" ? "locate" : "scan"}
          {r.speaker ? ` · ${r.speaker}` : ""}
          {r.errorMsg
            ? ` · ${r.errorMsg}`
            : r.detail
            ? ` · ${r.detail}`
            : typeof r.candidateCount === "number"
            ? ` · ${r.candidateCount} candidate${r.candidateCount === 1 ? "" : "s"}`
            : ""}
        </div>
      </div>
      {r.pendingCount > 0 && (
        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, color: "var(--ql-yellow)", background: "rgba(255,213,0,.12)", border: "1px solid rgba(255,213,0,.30)" }}>
          {r.pendingCount} to render
        </span>
      )}
      <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, color: b.color, background: `${b.color}1a`, border: `1px solid ${b.color}33` }}>{b.label}</span>
      {!isMobile && <span style={{ flexShrink: 0, fontFamily: "var(--ql-font-mono)", fontSize: 12, color: "var(--ql-fg-muted)", width: 40, textAlign: "right" }}>{timeAgo(r.createdAt)}</span>}
      {hasRender && (
        <IconBtn title="Open the existing render" onClick={(e) => { e.stopPropagation(); onOpenJob(r.relatedJobId!); }}>
          <Film size={13} strokeWidth={1.9} />
        </IconBtn>
      )}
      {canRetry && (
        <IconBtn title="Retry — re-run with the same URL & search terms" onClick={(e) => { void handleRetry(e); }}>
          {busy ? <Loader2 size={13} className="ql-spin" /> : <RotateCcw size={13} strokeWidth={1.9} />}
        </IconBtn>
      )}
      <IconBtn title="Copy URL" active={copied === `${r.id}-url`} onClick={(e) => { e.stopPropagation(); onCopy(r.url, `${r.id}-url`); }}>
        {copied === `${r.id}-url` ? <Check size={13} strokeWidth={2.6} /> : <Copy size={13} strokeWidth={1.9} />}
      </IconBtn>
      {r.speaker && (
        <IconBtn title="Copy speaker name" active={copied === `${r.id}-spk`} onClick={(e) => { e.stopPropagation(); onCopy(r.speaker!, `${r.id}-spk`); }}>
          {copied === `${r.id}-spk` ? <Check size={13} strokeWidth={2.6} /> : <span style={{ fontSize: 12, fontWeight: 800, lineHeight: 1 }}>@</span>}
        </IconBtn>
      )}
      <a href={r.url} target="_blank" rel="noreferrer" title="Open source on YouTube" onClick={(e) => e.stopPropagation()} style={iconBtnStyle(false)}>
        <ExternalLink size={13} strokeWidth={1.9} />
      </a>
      <IconBtn title="Delete from history" danger onClick={(e) => { e.stopPropagation(); onDelete(); }}>
        <Trash2 size={13} strokeWidth={1.9} />
      </IconBtn>
      {!isMobile && <ChevronRight size={16} strokeWidth={2} style={{ color: "var(--ql-fg-subtle)", flexShrink: 0 }} />}
    </div>
  );
}

function Tick({ checked, onChange }: { checked: boolean; onChange: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0, cursor: "pointer", padding: 0,
        border: checked ? "1px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.22)",
        background: checked ? "var(--ql-yellow)" : "transparent",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "all .12s var(--ql-ease)",
      }}
    >
      {checked && <Check size={12} strokeWidth={3.5} color="#000" />}
    </button>
  );
}

function IconBtn({ children, title, onClick, active, danger }: {
  children: React.ReactNode; title: string; onClick: (e: React.MouseEvent) => void; active?: boolean; danger?: boolean;
}): React.JSX.Element {
  return <button type="button" title={title} onClick={onClick} style={iconBtnStyle(!!active, danger)}>{children}</button>;
}

function iconBtnStyle(active: boolean, danger?: boolean): React.CSSProperties {
  const color = danger ? "#F87171" : active ? "#4ADE80" : "var(--ql-fg-muted)";
  return {
    width: 28, height: 28, borderRadius: 8, flexShrink: 0, cursor: "pointer",
    border: `1px solid ${danger ? "#F8717133" : "rgba(255,255,255,.08)"}`,
    background: danger ? "#F8717110" : "rgba(255,255,255,.02)",
    color, display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontFamily: "inherit", textDecoration: "none",
  };
}

function badge(status: RequestStatus): { label: string; color: string } {
  switch (status) {
    case "scanned": return { label: "Scanned", color: "#4ADE80" };
    case "failed": return { label: "Failed", color: "#F87171" };
    case "interrupted": return { label: "Interrupted", color: "#FB923C" };
    case "scanning": return { label: "Scanning", color: "#FBBF24" };
    case "transcribing": return { label: "Transcribing", color: "#FBBF24" };
    case "queued": return { label: "Queued", color: "#FBBF24" };
    default: return { label: String(status), color: "#9CA3AF" };
  }
}

function timeAgo(ts: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

const PANEL: React.CSSProperties = { background: "rgba(16,18,27,.72)", borderRadius: 18, border: "1px solid rgba(255,255,255,.06)", overflow: "hidden" };
const TOOLBAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,.06)" };
const DANGER_BTN: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, border: "1px solid #F8717140", background: "#F8717112", color: "#F87171", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer" };
const TOAST: React.CSSProperties = { position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 24, zIndex: 60, display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderRadius: 999, background: "rgba(16,18,27,.96)", border: "1px solid rgba(255,255,255,.12)", boxShadow: "0 8px 30px rgba(0,0,0,.5)" };
const UNDO_BTN: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, border: "1px solid rgba(255,213,0,.4)", background: "rgba(255,213,0,.12)", color: "var(--ql-yellow)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer" };

function Muted({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div style={{ color: "var(--ql-fg-muted)", fontSize: 13, padding: 40, textAlign: "center" }}>{children}</div>;
}

function Empty(): React.JSX.Element {
  return (
    <div style={{ background: "rgba(16,18,27,.72)", border: "1px dashed rgba(255,255,255,.10)", borderRadius: 18, padding: 60, textAlign: "center", color: "var(--ql-fg-muted)" }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ql-fg)", marginBottom: 6 }}>No scans yet.</div>
      <div style={{ fontSize: 13 }}>Run an Auto-scan or "Find a scene" from New job — it'll show up here with its candidates, and stick around across redeploys.</div>
    </div>
  );
}
